import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, connectionsAPI, calendarAPI } from '../api/client';
import { Footprints, Coffee, Mountain, Bike, UtensilsCrossed, Landmark, Smartphone } from 'lucide-react';
import '../styles/onboarding.css';

const ACTIVITY_TYPES = [
  { value: 'run', label: 'Run', Icon: Footprints },
  { value: 'coffee', label: 'Coffee', Icon: Coffee },
  { value: 'hike', label: 'Hike', Icon: Mountain },
  { value: 'cycling', label: 'Cycling', Icon: Bike },
  { value: 'dinner', label: 'Dinner', Icon: UtensilsCrossed },
  { value: 'museum', label: 'Museum', Icon: Landmark },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [sent, setSent] = useState(new Set());
  const [icsUrl, setIcsUrl] = useState('');
  const [addingIcs, setAddingIcs] = useState(false);
  const [contactMatches, setContactMatches] = useState([]);
  const [contactsScanned, setContactsScanned] = useState(null);
  const [importingContacts, setImportingContacts] = useState(false);
  const debounceRef = useRef(null);
  const contactsSupported = 'contacts' in navigator && 'ContactsManager' in window;

  useEffect(() => {
    // If returning from Google OAuth, skip to step 2
    if (searchParams.get('google') === 'connected') {
      setSearchParams({});
      setStep(2);
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length < 2) { setSearchResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await authAPI.search(searchQuery.trim());
        setSearchResults(res.data || []);
      } catch { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleAddFriend = async (userId) => {
    try {
      await connectionsAPI.add(userId);
      setSent((prev) => new Set([...prev, userId]));
    } catch {}
  };

  const handleImportContacts = async () => {
    setImportingContacts(true);
    try {
      const raw = await navigator.contacts.select(['name', 'email', 'tel'], { multiple: true });
      const contacts = raw.map(c => ({
        emails: c.email || [],
        phones: c.tel || [],
      }));
      setContactsScanned(raw.length);
      const res = await connectionsAPI.findByContacts(contacts);
      setContactMatches(res.data.matched || []);
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err);
    } finally {
      setImportingContacts(false);
    }
  };

  const handleAddIcsUrl = async () => {
    if (!icsUrl.trim()) { setStep(2); return; }
    setAddingIcs(true);
    try {
      await calendarAPI.addIcsUrl(icsUrl.trim(), 'My Calendar');
    } catch {}
    setAddingIcs(false);
    setStep(2);
  };

  const finish = () => navigate('/calendar');

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        {step === 1 && (
          <>
            <p className="onboarding-step">Step 1 of 4</p>
            <h1>Connect your calendar</h1>
            <p>Import your existing events so Covo knows when you're free.</p>

            <button
              className="btn-primary"
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}
              onClick={async () => {
                try {
                  const r = await calendarAPI.getGoogleAuthUrl('/onboarding');
                  window.location.href = r.data.url;
                } catch {
                  alert('Could not connect to Google Calendar. Please try the ICS URL option below.');
                }
              }}
            >
              Connect Google Calendar
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
              <div style={{ flex: 1, height: 1, background: '#eee' }} />
              <span style={{ fontSize: 12, color: '#bbb', flexShrink: 0 }}>or subscribe via ICS URL</span>
              <div style={{ flex: 1, height: 1, background: '#eee' }} />
            </div>

            <input
              className="onboarding-input"
              type="url"
              value={icsUrl}
              onChange={e => setIcsUrl(e.target.value)}
              placeholder="https://calendar.google.com/calendar/ical/…"
            />

            <div className="onboarding-actions" style={{ marginTop: 16 }}>
              <button className="onboarding-skip" onClick={() => setStep(2)}>Skip for now</button>
              <button className="btn-primary" onClick={handleAddIcsUrl} disabled={addingIcs}>
                {addingIcs ? 'Subscribing…' : 'Next'}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="onboarding-step">Step 2 of 4</p>
            <h1>What do you like to do?</h1>
            <p>We'll show you relevant activities in your feed.</p>
            <div className="type-grid">
              {ACTIVITY_TYPES.map((t) => (
                <button
                  key={t.value}
                  className={`type-tile${selectedTypes.includes(t.value) ? ' selected' : ''}`}
                  onClick={() => toggleType(t.value)}
                >
                  <span className="tile-emoji"><t.Icon size={24} strokeWidth={1.8} color={selectedTypes.includes(t.value) ? '#52AAD8' : '#888'} /></span>
                  <span className="tile-label">{t.label}</span>
                </button>
              ))}
            </div>
            <div className="onboarding-actions">
              <button className="onboarding-skip" onClick={() => setStep(3)}>Skip</button>
              <button className="btn-primary" onClick={() => setStep(3)}>Next</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <p className="onboarding-step">Step 3 of 4</p>
            <h1>Find your people</h1>
            <p>See who from your contacts is already on Covo.</p>

            {/* Phone contacts */}
            {contactsSupported && (
              <button
                className="btn-contacts-import"
                onClick={handleImportContacts}
                disabled={importingContacts}
              >
                <Smartphone size={20} strokeWidth={1.8} />
                {importingContacts ? 'Scanning…' : 'Import from phone contacts'}
              </button>
            )}

            {contactsScanned !== null && (
              <p className="contacts-scanned-note">
                Scanned {contactsScanned} contact{contactsScanned !== 1 ? 's' : ''} —{' '}
                {contactMatches.length > 0
                  ? `${contactMatches.length} on Covo`
                  : 'none on Covo yet'}
              </p>
            )}

            {contactMatches.map((u) => (
              <div key={u.id} className="friend-row">
                <div className="connection-avatar">{(u.display_name || '?')[0].toUpperCase()}</div>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{u.display_name}</span>
                {sent.has(u.id) ? (
                  <span style={{ fontSize: 12, color: '#52AAD8' }}>Sent ✓</span>
                ) : (
                  <button className="btn-primary btn-small" onClick={() => handleAddFriend(u.id)}>Add</button>
                )}
              </div>
            ))}

            {/* Social platforms */}
            <div className="social-import-section">
              <p className="social-import-label">Connect via social</p>
              <div className="social-import-grid">
                {[
                  { name: 'Instagram', abbr: 'IG' },
                  { name: 'Facebook', abbr: 'FB' },
                  { name: 'LinkedIn', abbr: 'in' },
                ].map(s => (
                  <div key={s.name} className="social-import-tile coming-soon">
                    <span className="social-tile-abbr">{s.abbr}</span>
                    <span className="social-tile-name">{s.name}</span>
                    <span className="social-tile-badge">Soon</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Manual search fallback */}
            <div className="social-import-section">
              <p className="social-import-label">Or search by name / email</p>
              <input
                className="onboarding-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email…"
              />
              {searchResults.map((u) => (
                <div key={u.id} className="friend-row">
                  <div className="connection-avatar">{(u.display_name || '?')[0].toUpperCase()}</div>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{u.display_name}</span>
                  {sent.has(u.id) ? (
                    <span style={{ fontSize: 12, color: '#52AAD8' }}>Sent ✓</span>
                  ) : (
                    <button className="btn-primary btn-small" onClick={() => handleAddFriend(u.id)}>Add</button>
                  )}
                </div>
              ))}
            </div>

            <div className="onboarding-actions" style={{ marginTop: 20 }}>
              <button className="onboarding-skip" onClick={() => setStep(4)}>Skip</button>
              <button className="btn-primary" onClick={() => setStep(4)}>Next</button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <p className="onboarding-step">Step 4 of 4</p>
            <h1>You're all set!</h1>
            <p>Your calendar is ready. Head over to see your week, or create your first covo.</p>
            <div className="onboarding-actions" style={{ marginTop: 24 }}>
              <button className="btn-tertiary" onClick={finish}>Go to calendar</button>
              <button className="btn-primary" onClick={() => navigate('/create-activity')}>
                Create first covo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
